<template>
    <div class="selector-stoplocation">
        <input-dropdown
            v-model="selectedStoplocation"
            class="input-dropdown"
            :loading="loading"
            :items="options"
            :has-groups="hasGroups"
            :no-result="noResult"
            :placeholder="texts.placeholder"
            :search="search"
        />

        <a
            v-if="showGeoLocationButton"
            class="get-location"
            :title="texts.geo_button_title"
            @click="getStopByGeo"
        >
            <font-awesome-icon icon="location-arrow" />
        </a>
    </div>
</template>

<script>
import debounce from "simple-debounce";
import InputDropdown from "@/components/inputs/InputDropdown";
import { mapActions, mapGetters } from "vuex";
import { LOCATION_TYPES } from "@/store/modules/location";

const debounceMilliseconds = 250;

// based on https://github.com/shentao/vue-multiselect/blob/14b407a9d14e6c4836ff265c7b22dc40c51b1a0f/src/multiselectMixin.js#L20
// but here we handle æøå better
// we have disabled the multiselect internal filter by setting interal-search to false
function filterStops(stops, query) {
    return stops.filter(s => includes(s.name, query));
}

function includes(str, query) {
    return normalize(str).indexOf(normalize(query)) !== -1;
}

function normalize(str) {
    return str
        .toString()
        .trim()
        .toLowerCase()
        .replace("æ", "ae")
        .replace("ø", "oe")
        .replace("å", "aa");
}

export default {
    components: {
        InputDropdown
    },
    props: {
        type: {
            type: String,
            required: true,
            validator: value => {
                // Only accepts values that are supported
                return LOCATION_TYPES.includes(value);
            }
        },
        enableGeo: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            skipGeo: false,
            geoBlocked: false,
            debouncer: debounce(this.debouncedSearch, debounceMilliseconds),
            searchStringTooLong: false,
            searchQuery: "",
            searchResults: [],
            loading: false,
            showHint: true
        };
    },
    computed: {
        texts() {
            return this.$t("selector_stoplocation");
        },
        showGeoLocationButton() {
            return Boolean(
                this.enableGeo &&
                    !this.geoBlocked &&
                    navigator.geolocation &&
                    !this.loading
            );
        },
        hasGroups() {
            return process.env.VUE_APP_SHOW_POPULAR_STOP_RESULTS === "true";
        },
        selectedStoplocation: {
            get() {
                return this.convertToOption(this.getLocationByType(this.type));
            },
            set(selectedStoplocation) {
                this.skipGeo = true;
                this.loading = false;
                const stopLocation = this.convertToStoplocation(
                    selectedStoplocation
                );
                this.setLocationByType({
                    type: this.type,
                    value: stopLocation
                });
                this.$emit("item-selected", stopLocation);
            }
        },
        options() {
            if (process.env.VUE_APP_SHOW_POPULAR_STOP_RESULTS === "true") {
                if (
                    this.searchResults !== undefined &&
                    this.searchResults.length > 0
                ) {
                    return filterStops(
                        this.searchResults,
                        this.searchQuery
                    ).map(this.convertToOption);
                }

                if (this.getPopularStops !== undefined) {
                    return filterStops(
                        this.getPopularStops,
                        this.searchQuery
                    ).map(this.convertToOption);
                }

                return [];
            } else {
                return filterStops(this.searchResults, this.searchQuery).map(
                    this.convertToOption
                );
            }
        },
        noResult() {
            if (this.loading && this.searchQuery && !this.searchStringTooLong) {
                return this.texts.searching;
            } else if (this.searchStringTooLong) {
                return this.texts.search_string_too_long;
            } else if (this.searchQuery) {
                return this.texts.no_result;
            } else {
                return this.texts.search_hint;
            }
        },
        ...mapGetters({
            getLocationByType: "location/getLocationByType",
            getPopularStops: "location/getPopularStops",
            getMaxStopSearchLength: "system/getMaxStopSearchLength"
        })
    },
    mounted() {
        if (this.enableGeo && !this.selectedStoplocation) {
            this.getStopByGeo();
        }
    },
    methods: {
        convertToStoplocation(element) {
            // We need both popular and search results when we convert back
            // since we do not know which list a particular stop belongs to
            const results = [...this.getPopularStops, ...this.searchResults];

            return results.filter(stoplocation => {
                return stoplocation.id === element.id;
            })[0];
        },
        convertToOption(element) {
            return (
                element && {
                    id: element.id,
                    label: element.name,
                    type: element.type
                }
            );
        },
        search(search) {
            this.searchQuery = search;
            this.searchStringTooLong =
                search.length > this.getMaxStopSearchLength;
            this.showHint = false;
            if (this.searchStringTooLong) {
                return;
            }

            if (
                process.env.VUE_APP_SHOW_POPULAR_STOP_RESULTS === "true" ||
                search !== ""
            ) {
                this.loading = true;
                this.debouncer(search);
            }
        },
        debouncedSearch(search) {
            this.searchLocations({
                search: search
            })
                .then(searchResults => {
                    this.searchResults = searchResults;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        getStopByGeo() {
            this.skipGeo = false;
            if (!navigator.geolocation) return;
            this.loading = true;
            navigator.geolocation.getCurrentPosition(
                position => {
                    if (this.skipGeo) {
                        this.loading = false;
                        return;
                    }

                    this.setLocationByTypeAndGeo({
                        type: this.type,
                        longitude: position.coords.longitude,
                        latitude: position.coords.latitude
                    }).finally(() => {
                        this.loading = false;
                    });
                },
                err => {
                    if (err && err.code === 1) this.geoBlocked = true;
                    this.loading = false;
                }
            );
        },
        ...mapActions({
            searchLocations: "location/searchLocations",
            setLocationByType: "location/setLocationByType",
            setLocationByTypeAndGeo: "location/setLocationByTypeAndGeo"
        })
    }
};
</script>

<style scoped>
.selector-stoplocation {
    width: 100%;
    display: flex;
    align-items: center;
}

.input-dropdown {
    flex: 1 1 auto;
}

.get-location {
    flex: 0 0 30px;
    text-align: center;
    font-size: 14px;
    color: #000;
    cursor: pointer;
}
</style>
